// render-card.ts — builds one catalog card's DOM: link → card → video-or-image →
// bottom-right name overlay button (optionally a leading icon + corner badge). This is
// now the canonical card markup; it was originally cloned element-for-element from the
// legacy options/table_lists/table_list_ancestries_inc.php (since removed). Built with the
// DOM API (createElement + textContent/setAttribute) rather than HTML strings, so item
// names are escaped intrinsically — no jQuery, no manual escaping.

import type { CatalogCard } from './types';

export function renderCatalogCard(card: CatalogCard): HTMLAnchorElement {
    const hasVideo = !!card.video_url;

    const link = document.createElement('a');
    link.href = card.item_url;
    link.className = 'text-decoration-none';

    const cardEl = document.createElement('div');
    // ft-fade-overlay-on-play (Fable 2026-07-01): OPT-IN marker for the name/badge overlay fade while
    // the video plays (initializeVideoCards toggles .ft-overlay-faded; CSS keys on the PAIR). Only
    // this builder (classes/subclasses catalog cards) opts in, so adventures/library/source video
    // cards are untouched — the scope guard the feature entry asked for.
    cardEl.className = hasVideo ? 'card text-left mb-3 video-card-hover ft-fade-overlay-on-play' : 'card text-left mb-3';

    if (hasVideo) {
        const video = document.createElement('video');
        video.className = 'card-img d-block w-100';
        video.muted = true;
        video.loop = true;
        video.setAttribute('playsinline', '');
        // Poster-less cards fetch their first frame near the viewport (initializeVideoCards).
        // Starting every card cold avoids a page-wide burst of concurrent CDN range requests.
        video.setAttribute('preload', 'none');
        if (!card.picture_url) video.dataset.ftLazyPreload = 'metadata';
        if (card.picture_url) video.setAttribute('poster', card.picture_url);
        const source = document.createElement('source');
        source.src = card.video_url as string;
        source.type = card.video_mime || 'video/mp4';
        video.appendChild(source);
        cardEl.appendChild(video);
    } else {
        const img = document.createElement('img');
        img.src = card.picture_url || '';
        img.className = 'card-img';
        img.alt = card.item_name || 'Item media';
        img.loading = 'lazy';
        cardEl.appendChild(img);
    }

    const overlay = document.createElement('div');
    overlay.className = 'card-img-overlay';
    const overlayInner = document.createElement('div');
    overlayInner.className = 'position-absolute bottom-0 end-0 me-3 mb-3';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'btn btn-sm border btn-info position-relative';
    // Optional leading icon (classes/subclasses show an icon beside the name). Resolve it client-side
    // from `icon_id` via iconMap() (pure-data payload, full JS/TS); fall back to a legacy pre-rendered
    // `icon_html` for endpoints not yet migrated. Inject the trusted icon HTML, then append the name
    // as an escaped text node.
    const iconHtml = card.icon_id ? iconMap(card.icon_id) : (card.icon_html || '');
    if (iconHtml) {
        const icon = document.createElement('span');
        icon.className = 'me-1';
        icon.innerHTML = iconHtml;
        btn.appendChild(icon);
        btn.appendChild(document.createTextNode(card.item_name || 'Untitled'));
    } else {
        btn.textContent = card.item_name || 'Untitled';
    }
    // Optional corner badge (subclasses: the required-class icon, top-right of the button). Same
    // contract as the leading icon — `badge_id` resolved via iconMap(), legacy `badge_html` honoured.
    const badgeHtml = card.badge_id ? iconMap(card.badge_id) : (card.badge_html || '');
    if (badgeHtml) {
        const badge = document.createElement('span');
        badge.className = 'position-absolute top-0 end-0 translate-middle';
        badge.innerHTML = badgeHtml;
        btn.appendChild(badge);
    }
    overlayInner.appendChild(btn);
    overlay.appendChild(overlayInner);
    cardEl.appendChild(overlay);

    link.appendChild(cardEl);
    return link;
}
